import { useEffect, useState } from 'react'
import { Link, Navigate, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { AlertTriangle, ArrowLeft, ArrowRight, CheckCircle2, Loader2, XCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { useBillingOrder, type BillingOrder } from '@/hooks/use-billing'
import { useAiCredits } from '@/hooks/use-ai-credits'
import { walletHeadline } from '@/lib/ai-credits'
import { cn } from '@/lib/utils'
import { BankTransferPanel } from './bank-transfer-panel'
import { OrderActions, useRenewAndPay } from './order-actions'
import { orderKindOf, paidLinks, paidSummary, retryLink } from './order-summary'
import { methodShortLabel, parsePaymentReturn, readPendingOrder, readReturnFrom, safeFrom, writePendingOrder } from './payment-methods'

/**
 * Kết quả thanh toán — trang chung cho MỌI loại đơn (mua / đổi gói, mua thêm, nạp credit AI):
 *  - 2C2P / PayPal: cổng đưa khách về /billing/result?payment=return&order=…&evopay_status=…
 *  - Chuyển khoản: /billing/result?payment=bank&order=… (khung QR, tự chuyển sang kết quả khi tiền về)
 *  - Thông báo "đã thanh toán" trỏ về /billing/result?order=…
 * `from` (?from= hoặc đã lưu lúc bấm thanh toán) = trang khách đang làm dở → nút chính "Tiếp tục việc đang làm".
 * Trạng thái lấy từ server (webhook / đối soát) — KHÔNG tin tham số trên URL.
 */
export function PaymentResultPage() {
  const { t } = useTranslation('billing')
  const [params] = useSearchParams()
  const qc = useQueryClient()
  const [ret] = useState(() => {
    // Liên kết từ thông báo chỉ có ?order= (không có payment=) → coi như quay về từ cổng.
    const p = new URLSearchParams(params)
    if (!p.has('payment')) p.set('payment', 'return')
    return parsePaymentReturn(p)
  })
  const [trackId, setTrackId] = useState<string | null>(() => ret?.orderRef ?? readPendingOrder())
  const [from] = useState(() => safeFrom(params.get('from')) ?? readReturnFrom())
  // Huỷ / lỗi: chỉ hỏi server một lần (server vẫn đối soát với cổng), không chờ tiếp.
  const poll = ret?.result !== 'cancel' && ret?.result !== 'error'
  const { data: order, isError, refetch } = useBillingOrder(trackId, { poll })
  const renew = useRenewAndPay()
  const [rechecking, setRechecking] = useState(false)
  const settled = !!order && (order.status !== 'pending' || !poll)

  useEffect(() => {
    if (!settled) return
    writePendingOrder(null)
    qc.invalidateQueries({ queryKey: ['billing-summary'] })
    qc.invalidateQueries({ queryKey: ['billing-orders'] })
    if (order && orderKindOf(order) === 'ai') qc.invalidateQueries({ queryKey: ['ai', 'credits'] })
  }, [settled, order, qc])

  if (!trackId) return <Navigate to="/billing" replace />

  const gateway = order?.provider === 'evopay_card' ? '2C2P'
    : order?.provider === 'evopay_bank' ? t('paymentResult.gatewayBank')
    : order?.provider === 'evopay_paypal' ? 'PayPal'
    : order?.provider === 'evopay_sepay' ? 'SePay'
    : order?.provider === 'evopay_tingee' ? 'Tingee'
    : t('paymentResult.gatewayGeneric')

  // Chuyển khoản còn chờ → khung QR (vẫn tự hỏi trạng thái đơn); tiền về thì tự chuyển sang kết quả.
  let body: React.ReactNode
  if (order?.status === 'pending' && order.bankTransfer && poll) {
    const recheck = async () => {
      setRechecking(true)
      const r = await refetch()
      setRechecking(false)
      if (r.data?.status === 'pending') toast.info(t('paymentResult.bankNotYetReceived'))
    }
    body = (
      <BankTransferPanel
        info={order.bankTransfer} expiresAt={order.expiresAt} orderCode={order.paymentCode}
        onClose={() => { setTrackId(null); writePendingOrder(null) }}
        onRenew={() => renew.run(order.id)} renewing={renew.pending}
        onRecheck={recheck} rechecking={rechecking}
      />
    )
  } else if (isError) {
    body = (
      <ResultCard
        tone="error" icon={<XCircle className="h-8 w-8 text-destructive" />}
        title={t('paymentResult.cannotCheckTitle')}
        desc={t('paymentResult.cannotCheckDesc')}
        actions={<Button asChild variant="outline"><Link to="/billing">{t('orderSummary.backToPlansPage')}</Link></Button>}
      />
    )
  } else if (order?.status === 'paid') {
    const { title, desc } = paidSummary(order)
    const links = paidLinks(orderKindOf(order), from)
    body = (
      <ResultCard
        tone="success" icon={<CheckCircle2 className="h-8 w-8 text-success" />} title={title} desc={desc}
        details={<PaidDetails order={order} />}
        actions={
          <>
            <Button asChild size="lg"><Link to={links.primary.to}>{links.primary.label} <ArrowRight className="ml-1.5 h-4 w-4" /></Link></Button>
            {links.secondary.map((l) => <Button key={l.to + l.label} asChild variant="outline" size="lg"><Link to={l.to}>{l.label}</Link></Button>)}
          </>
        }
      />
    )
  } else if (order?.status === 'underpaid') {
    body = (
      <ResultCard
        tone="error" icon={<AlertTriangle className="h-8 w-8 text-destructive" />} title={t('paymentResult.underpaidTitle')}
        desc={t('paymentResult.underpaidDesc', { code: order.paymentCode ?? order.id })}
        actions={<Button asChild variant="outline"><Link to="/billing">{t('orderSummary.backToPlansPage')}</Link></Button>}
      />
    )
  } else if (order && order.status !== 'pending') {
    const retry = retryLink(order, from)
    body = (
      <ResultCard
        tone="error" icon={<XCircle className="h-8 w-8 text-destructive" />}
        title={order.status === 'expired' ? t('paymentResult.expiredTitle') : order.status === 'cancelled' ? t('paymentResult.cancelledTitle') : t('paymentResult.failedTitle')}
        desc={t('paymentResult.failedDesc')}
        actions={<><OrderActions order={order} size="default" /><Button asChild variant="outline"><Link to={retry.to}>{retry.label}</Link></Button></>}
      />
    )
  } else if (!poll && ret) {
    // Khách huỷ / cổng báo lỗi, đơn vẫn chờ phía server.
    body = (
      <ResultCard
        tone="error" icon={<XCircle className="h-8 w-8 text-destructive" />}
        title={ret.result === 'cancel' ? t('paymentResult.youCancelledTitle') : t('paymentResult.gatewayErrorTitle')}
        desc={t('paymentResult.notPaidDesc', { gateway })}
        actions={order ? <OrderActions order={order} size="default" /> : null}
      />
    )
  } else {
    body = (
      <ResultCard
        tone="pending" icon={<Loader2 className="h-8 w-8 animate-spin text-primary" />}
        title={t('paymentResult.waitingTitle', { gateway })}
        desc={t('paymentResult.waitingDesc')}
        actions={order ? <OrderActions order={order} size="default" /> : null}
      />
    )
  }

  return (
    <div className="-m-4 flex min-h-full flex-col bg-card md:-m-6">
      <header className="flex h-14 shrink-0 items-center gap-3 border-b px-5">
        <Link to="/billing" className="flex items-center gap-1.5 text-sm text-foreground hover:text-primary">
          <ArrowLeft className="h-4 w-4" /> {t('paymentResult.plansNav')}
        </Link>
        <span className="h-4 w-px bg-border" />
        <h1 className="text-base font-medium">{t('paymentResult.pageTitle')}</h1>
      </header>
      <div className="mx-auto w-full max-w-xl px-4 py-6 md:py-10">{body}</div>
    </div>
  )
}

function ResultCard({ tone, icon, title, desc, details, actions }: {
  tone: 'success' | 'error' | 'pending'
  icon: React.ReactNode
  title: string
  desc: string
  details?: React.ReactNode
  actions?: React.ReactNode
}) {
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={cn(
        'rounded-xl border p-6 text-center',
        tone === 'success' && 'border-success/30 bg-success/5',
        tone === 'error' && 'border-destructive/30 bg-destructive/5',
        tone === 'pending' && 'border-primary/30 bg-primary/5',
      )}
    >
      <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background shadow-sm">{icon}</span>
      <h2 className="mt-4 text-lg font-semibold">{title}</h2>
      <p className="mt-1 text-sm text-muted-foreground">{desc}</p>
      {details}
      {actions && <div className="mt-6 flex flex-col items-stretch justify-center gap-2 sm:flex-row sm:items-center empty:hidden">{actions}</div>}
    </div>
  )
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 px-4 py-2.5">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right font-medium tabular-nums">{children}</dd>
    </div>
  )
}

/** Biên lai ngắn: mã đơn, phương thức, thời điểm, (credit) số dư mới. */
function PaidDetails({ order }: { order: BillingOrder }) {
  const { t } = useTranslation('billing')
  return (
    <dl className="mt-5 divide-y rounded-lg border bg-background text-left text-sm">
      <Row label={t('paymentResult.orderCode')}><span className="font-mono">{order.paymentCode ?? order.id.slice(0, 8).toUpperCase()}</span></Row>
      <Row label={t('paymentResult.method')}>{methodShortLabel(order.provider)}</Row>
      {order.paidAt && <Row label={t('paymentResult.paidAt')}>{new Date(order.paidAt).toLocaleString('vi-VN', { dateStyle: 'short', timeStyle: 'short' })}</Row>}
      {orderKindOf(order) === 'ai' && <AiBalanceRow />}
    </dl>
  )
}

function AiBalanceRow() {
  const { t } = useTranslation('billing')
  const { data } = useAiCredits()
  if (!data) return null
  const head = walletHeadline(data)
  return <Row label={t('paymentResult.aiBalance')}>{head.main}</Row>
}
